import moment from 'moment';
import type { AppNotification } from '../redux/notificationsSlice';

// What a notification card shows, worked out from the stored notice. Payment
// notices carry structured fields (amount, payer, outcome …) since 2026-10-15;
// older ones, and notices of other kinds, fall back to their title and body.

export type PaymentTone = 'paid' | 'partial' | 'unmatched' | 'received';

export type CardReference = { label: string; value: string };

export type PaymentCardModel = {
  kind: 'payment';
  payer: string;
  amount: number;
  tone: PaymentTone;
  /** Partial payments: what the order still owes. */
  amountDue: number;
  /** Paid orders: change handed back (never STK rounding). */
  change: number;
  references: CardReference[];
  /** Shown under the references (unmatched payments). */
  hint: string | null;
};

export type OrderCardModel = {
  kind: 'order';
  /** Who and what, e.g. "Amina · Table 4". */
  subtitle: string;
  references: CardReference[];
};

export type PlainCardModel = { kind: 'plain' };

export type NotificationCardModel =
  | PaymentCardModel
  | OrderCardModel
  | PlainCardModel;

const PAYMENT_TONES: PaymentTone[] = [
  'paid',
  'partial',
  'unmatched',
  'received',
];

const readText = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

// Push data arrives as strings; stored notices keep numbers.
const readAmount = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

function buildPaymentModel(data: Record<string, any>): PaymentCardModel | null {
  const tone = readText(data.outcome) as PaymentTone;
  if (!PAYMENT_TONES.includes(tone)) return null;

  const references: CardReference[] = [];
  const receiptRef = readText(data.receiptRef);
  const paidTo = readText(data.paidTo);
  const mpesaReceipt = readText(data.mpesaReceipt);
  if (receiptRef) references.push({ label: 'Receipt ID', value: receiptRef });
  else if (paidTo) references.push({ label: 'Paid to', value: paidTo });
  if (mpesaReceipt) {
    references.push({ label: 'M-Pesa transaction code', value: mpesaReceipt });
  }

  return {
    kind: 'payment',
    payer: readText(data.payer),
    amount: readAmount(data.amount),
    tone,
    amountDue: tone === 'partial' ? readAmount(data.amountDue) : 0,
    change: tone === 'paid' ? readAmount(data.change) : 0,
    references,
    // The server gives the steps for money it could not add to the order it
    // names; older notices keep the default.
    hint:
      readText(data.hint) ||
      (tone === 'unmatched'
        ? 'Select it at checkout to link it to an order.'
        : null),
  };
}

function buildOrderModel(data: Record<string, any>): OrderCardModel | null {
  const receiptRef = readText(data.receiptRef);
  const subtitle = [readText(data.waiterName), readText(data.customer)]
    .filter(Boolean)
    .join(' · ');
  if (!receiptRef && !subtitle) return null;
  return {
    kind: 'order',
    subtitle,
    references: receiptRef ? [{ label: 'Receipt ID', value: receiptRef }] : [],
  };
}

export function buildNotificationCardModel(
  item: Pick<AppNotification, 'data' | 'type'>,
): NotificationCardModel {
  const data = item.data || {};
  const type = readText(data.type) || readText(item.type);
  if (type === 'payment_received') {
    return buildPaymentModel(data) || { kind: 'plain' };
  }
  if (type === 'waiter_order') {
    return buildOrderModel(data) || { kind: 'plain' };
  }
  return { kind: 'plain' };
}

/** "Just now", "14 min ago", "3 h ago" today; the clock time before that. */
export function formatNotificationTime(
  createdAt: number,
  now: number = Date.now(),
): string {
  const created = moment(createdAt);
  if (!created.isSame(moment(now), 'day')) return created.format('HH:mm');
  const minutes = Math.floor((now - createdAt) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.floor(minutes / 60)} h ago`;
}

/** Section heading for the day a notice arrived. */
export function notificationDayLabel(
  createdAt: number,
  now: number = Date.now(),
): string {
  const created = moment(createdAt);
  const today = moment(now);
  if (created.isSame(today, 'day')) return 'Today';
  if (created.isSame(today.clone().subtract(1, 'day'), 'day')) {
    return 'Yesterday';
  }
  return created.format(
    created.isSame(today, 'year') ? 'ddd D MMM' : 'ddd D MMM YYYY',
  );
}

export type NotificationSection = {
  title: string;
  data: AppNotification[];
};

/** Newest-first notices grouped by day, keeping their order. */
export function groupNotificationsByDay(
  items: AppNotification[],
  now: number = Date.now(),
): NotificationSection[] {
  const sections: NotificationSection[] = [];
  for (const item of items) {
    const title = notificationDayLabel(item.createdAt, now);
    const last = sections[sections.length - 1];
    if (last && last.title === title) last.data.push(item);
    else sections.push({ title, data: [item] });
  }
  return sections;
}
